import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../../supabaseClient';
import { usePermissions } from '../../../utils/permissions';
import {
  Box, Collapse, Container, Card, CardContent, Typography, Button,
  Alert, CircularProgress, LinearProgress, Fade,
  alpha, useTheme
} from '@mui/material';
import {
  Save as SaveIcon, Print as PrintIcon, Receipt as ReceiptIcon,
  ExpandMore as ExpandMoreIcon, ArrowBack as ArrowBackIcon,
  AutoFixHigh as AutoFillIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import DetailsSections from './invoice-creation-components/DetailsSections';
import CustomerInformationSection from './invoice-creation-components/CustomerInformationSection';
import LineItemSection from './invoice-creation-components/LineItemSection';
import PaymentTermsSection from './invoice-creation-components/PaymentTermsSection';
import SummarySection from './invoice-creation-components/SummarySection';
import AdditionalNotesSection from './invoice-creation-components/AdditionalNotesSection';
import TermsAndConditionsSection from './invoice-creation-components/TermsAndConditionsSection';
import PreparedBySection from './invoice-creation-components/PreparedBySection';
import PrintPreview from './invoice-creation-components/PrintPreview';
import AutofillOrderDialogue from './invoice-creation-components/AutofillOrderDialogue';
import { printInvoice } from './invoice-creation-components/printInvoice';
import { calculateInvoiceTotals, getTodayString } from './invoice-creation-components/utils';

const ACCORDION_KEY = 'invoice-creation-accordion';

const getInitialAccordionState = () => {
  try {
    const stored = localStorage.getItem(ACCORDION_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch { return {}; }
};

const CollapsibleSection = ({ title, expanded, onToggle, children }) => {
  const theme = useTheme();
  return (
    <Box sx={{ mt: 2 }}>
      <Box onClick={onToggle} sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        py: 1.25, px: 2.5, cursor: 'pointer', userSelect: 'none',
        borderRadius: expanded ? '12px 12px 0 0' : '12px',
        background: 'white',
        border: `1px solid ${alpha(theme.palette.grey[300], 0.9)}`,
        borderBottom: expanded ? `1px solid ${alpha(theme.palette.primary.main, 0.12)}` : `1px solid ${alpha(theme.palette.grey[300], 0.9)}`,
        boxShadow: expanded ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.08)}` : `0 1px 4px ${alpha(theme.palette.grey[400], 0.15)}`,
        transition: 'box-shadow 0.2s, border-radius 0.2s',
        '&:hover': { boxShadow: `0 2px 10px ${alpha(theme.palette.primary.main, 0.12)}`, borderColor: alpha(theme.palette.primary.main, 0.3) }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: expanded ? 'primary.main' : alpha(theme.palette.grey[400], 0.8), transition: 'background-color 0.2s', flexShrink: 0 }} />
          <Typography fontWeight={500} sx={{ fontSize: '0.9rem', color: expanded ? 'primary.main' : 'text.secondary', transition: 'color 0.2s' }}>
            {title}
          </Typography>
        </Box>
        <ExpandMoreIcon sx={{ fontSize: 18, color: expanded ? 'primary.main' : 'text.disabled', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s, color 0.2s' }} />
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ '& > *:first-of-type': { mt: '0 !important', borderRadius: '0 0 12px 12px !important', borderTop: 'none !important', animation: 'none !important' } }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};

const InvoiceCreation = ({ currentCompany }) => {
  const { companyId, id: invoiceId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { hasPermission } = usePermissions();

  const isEdit = invoiceId && invoiceId !== 'new';

  const [invoice, setInvoice] = useState({
    invoice_number: '',
    purchase_order_number: '',
    bill_of_lading_number: '',
    invoice_date: getTodayString(),
    due_date: '',
    status: 'pending',
    customer_id: '',
    customer_name: '',
    customer_company: '',
    billing_street: '', billing_city: '', billing_state: '', billing_zip: '', billing_country: '',
    shipping_street: '', shipping_city: '', shipping_state: '', shipping_zip: '', shipping_country: '',
    notes: '', special_instructions: '', internal_notes: '',
    subtotal: 0, discount: 0, discount_amount: 0, discount_type: 'percent',
    tax_rate: 0, tax_amount: 0,
    shipping_fee: 0, handling_fee: 0,
    total_before_credit: 0, total_amount: 0, amount_due: 0,
    price_list_id: '', price_list_name: '',
    payment_terms: 'Net 30', payment_method: '',
    late_fee_rate: 0, early_payment_discount: 0, early_payment_days: 0,
    terms_and_conditions: '',
    prepared_by: null
  });

  const [lineItems, setLineItems] = useState([]);
  const lineItemsRef = useRef(lineItems);
  const [customers, setCustomers] = useState([]);
  const [priceLists, setPriceLists] = useState([]);
  const [company, setCompany] = useState(currentCompany || null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [autofillOpen, setAutofillOpen] = useState(false);
  const [accordion, setAccordion] = useState(getInitialAccordionState);

  const toggleSection = useCallback((key) => {
    setAccordion(prev => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem(ACCORDION_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const canView = hasPermission('sales.invoices.view');
  const canEdit = hasPermission('sales.invoices.edit', 'write');
  const canDelete = hasPermission('sales.invoices.delete', 'delete');

  useEffect(() => {
    const loadData = async () => {
      if (!companyId) return;
      try {
        await Promise.all([
          isEdit ? fetchInvoice() : generateInvoiceNumber(),
          fetchCompany(),
          fetchCustomers(),
          fetchPriceLists()
        ]);
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load data: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [companyId, isEdit, invoiceId]);


  const generateInvoiceNumber = async () => {
    try {
      const now = new Date();
const yearStart = `${now.getFullYear()}-01-01`;
      const yearEnd = `${now.getFullYear()}-12-31`;
      const { count } = await supabase.from('invoices').select('id', { count: 'exact', head: true })
        .eq('company_id', companyId)
        .gte('invoice_date', yearStart)
        .lte('invoice_date', yearEnd);
      const next = (count || 0) + 1;
      setInvoice(prev => ({ ...prev, invoice_number: `INV-${now.getFullYear()}-${String(next).padStart(4, '0')}` }));
    } catch (err) {
      const now = new Date();
      setInvoice(prev => ({ ...prev, invoice_number: `INV-${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}-${String(Date.now()).slice(-4)}` }));
    }
  };

  const fetchInvoice = async () => {
    try {
      const { data: inv, error: invErr } = await supabase.from('invoices').select('*').eq('id', invoiceId).single();
      if (invErr) throw invErr;
      if (inv) {
        setInvoice(inv);
        if (inv.customer_id) {
          fetchAndFillCustomerBilling(inv.customer_id);
        }
        const { data: items, error: itemsErr } = await supabase
          .from('invoice_items').select('*').eq('invoice_id', invoiceId).order('line_order');
        if (itemsErr) throw itemsErr;
        setLineItems(items || []);
      }
    } catch (err) {
      console.error('Error fetching invoice:', err);
      setError('Failed to load invoice: ' + err.message);
    }
  };

  const fetchCompany = async () => {
    if (!companyId) return;
    try {
      const { data } = await supabase.from('companies').select('*').eq('id', companyId).single();
      if (data) setCompany(data);
    } catch (err) { console.error('Error fetching company:', err); }
  };

  const fetchCustomers = async () => {
    try {
      const { data } = await supabase.from('customers').select('*').eq('company_id', companyId).order('name');
      setCustomers(data || []);
    } catch (err) { console.error('Error fetching customers:', err); }
  };

  const fetchPriceLists = async () => {
    try {
      const { data } = await supabase.from('price_lists').select('*').eq('company_id', companyId).eq('is_active', true).order('name');
      setPriceLists(data || []);
    } catch (err) { console.error('Error fetching price lists:', err); }
  };

  const fetchAndFillCustomerBilling = async (customerId) => {
    try {
      const { data: customer } = await supabase.from('customers').select('street, city, state, zip_code, country, payment_terms').eq('id', customerId).single();
      if (!customer) return;
      setInvoice(prev => {
        const billingEmpty = !prev.billing_street && !prev.billing_city;
        return {
          ...prev,
          ...(billingEmpty ? {
            billing_street: customer.street || '', billing_city: customer.city || '',
            billing_state: customer.state || '', billing_zip: customer.zip_code || '',
            billing_country: customer.country || ''
          } : {}),
          payment_terms: customer.payment_terms || prev.payment_terms
        };
      });
    } catch (err) { console.error('Error fetching customer billing:', err); }
  };

  const handleAutofill = useCallback((invoiceData, items) => {
    setInvoice(prev => ({ ...prev, ...invoiceData }));
    // Merge duplicate product+variant+price rows (lot numbers don't matter for invoices)
    const merged = [];
    (items || []).forEach(newItem => {
      const existingIdx = merged.findIndex(li =>
        li.product_id === newItem.product_id &&
        li.variant_id === newItem.variant_id &&
        li.unit_type === newItem.unit_type &&
        parseFloat(li.unit_price) === parseFloat(newItem.unit_price)
      );
      if (existingIdx >= 0) {
        const existing = merged[existingIdx];
        const qty = (parseFloat(existing.quantity) || 0) + (parseFloat(newItem.quantity) || 0);
        const price = parseFloat(existing.unit_price) || 0;
        merged[existingIdx] = { ...existing, quantity: qty, line_total: qty * price, total_price: qty * price };
      } else {
        merged.push(newItem);
      }
    });
    setLineItems(merged);
  }, []);

  useEffect(() => { lineItemsRef.current = lineItems; }, [lineItems]);

  const recalculateTotals = useCallback((items, inv) => {
    const totals = calculateInvoiceTotals(items, inv.discount, inv.tax_rate, inv.shipping_fee, inv.handling_fee, undefined, inv.discount_type || 'percent');
    setInvoice(prev => ({ ...prev, ...totals }));
  }, []);

  useEffect(() => {
    recalculateTotals(lineItems, invoice);
  }, [lineItems, invoice.discount, invoice.tax_rate, invoice.shipping_fee, invoice.handling_fee]);

  const handleInvoiceUpdate = (updates) => {
    setInvoice(prev => {
      const updated = { ...prev, ...updates };
      if (updates.discount !== undefined || updates.tax_rate !== undefined || updates.shipping_fee !== undefined || updates.handling_fee !== undefined) {
        recalculateTotals(lineItems, updated);
      }
      if (updates.customer_id && updates.customer_id !== prev.customer_id) {
        fetchAndFillCustomerBilling(updates.customer_id);
      }
      return updated;
    });
  };

  const handleLineItemChange = (index, field, value) => {
    const updated = [...lineItems];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'quantity' || field === 'unit_price') {
      const qty = parseFloat(updated[index].quantity);
      const price = parseFloat(updated[index].unit_price);
      updated[index].line_total = (!isNaN(qty) && !isNaN(price) && qty > 0 && price >= 0) ? Number((qty * price).toFixed(2)) : 0;
      updated[index].total_price = updated[index].line_total;
    }
    setLineItems(updated);
    recalculateTotals(updated, invoice);
  };

  const handleRemoveLineItem = (index) => {
    const updated = [...lineItems];
    updated.splice(index, 1);
    setLineItems(updated);
    recalculateTotals(updated, invoice);
  };

  const sanitizeUUIDs = (data) => {
    const uuidFields = ['price_list_id', 'customer_id', 'prepared_by'];
    const sanitized = { ...data };
    uuidFields.forEach(field => {
      if (sanitized[field] === '' || sanitized[field] === undefined) sanitized[field] = null;
    });
    return sanitized;
  };

  const handleSave = async () => {
    if (!invoice.invoice_number) { setError('Invoice number is required'); return; }
    if (!invoice.customer_id) { setError('Please select a customer'); return; }

    setSaving(true);
    setError(null);

    try {
      let invoiceDbId = invoice.id || (invoiceId && invoiceId !== 'new' ? invoiceId : null);

      const invoiceData = sanitizeUUIDs({
        company_id: companyId,
        invoice_number: invoice.invoice_number,
        purchase_order_number: invoice.purchase_order_number || null,
        bill_of_lading_number: invoice.bill_of_lading_number || null,
        invoice_date: invoice.invoice_date || null,
        due_date: invoice.due_date || null,
        status: invoice.status || 'pending',
        customer_id: invoice.customer_id,
        customer_name: invoice.customer_name || '',
        billing_street: invoice.billing_street || '', billing_city: invoice.billing_city || '',
        billing_state: invoice.billing_state || '', billing_zip: invoice.billing_zip || '',
        billing_country: invoice.billing_country || '',
        shipping_street: invoice.shipping_street || '', shipping_city: invoice.shipping_city || '',
        shipping_state: invoice.shipping_state || '', shipping_zip: invoice.shipping_zip || '',
        shipping_country: invoice.shipping_country || '',
        notes: invoice.notes || '', special_instructions: invoice.special_instructions || '',
        internal_notes: invoice.internal_notes || '',
        subtotal: parseFloat(invoice.subtotal) || 0,
        discount: parseFloat(invoice.discount) || 0,
        discount_type: invoice.discount_type || 'percent',
        tax_rate: parseFloat(invoice.tax_rate) || 0,
        tax_amount: parseFloat(invoice.tax_amount) || 0,
        shipping_fee: parseFloat(invoice.shipping_fee) || 0,
        handling_fee: parseFloat(invoice.handling_fee) || 0,
        total_before_credit: parseFloat(invoice.total_before_credit) || 0,
        total_amount: parseFloat(invoice.total_amount) || 0,
        amount_due: parseFloat(invoice.amount_due) || 0,
        price_list_id: invoice.price_list_id || null,
        payment_terms: invoice.payment_terms || '',
        payment_method: invoice.payment_method || '',
        late_fee_rate: parseFloat(invoice.late_fee_rate) || 0,
        early_payment_discount: parseFloat(invoice.early_payment_discount) || 0,
        early_payment_days: parseInt(invoice.early_payment_days) || 0,
        terms_and_conditions: invoice.terms_and_conditions || '',
        prepared_by: invoice.prepared_by || null
      });

      if (invoiceDbId) {
        const { error: updateErr } = await supabase.from('invoices').update({ ...invoiceData, updated_at: new Date().toISOString() }).eq('id', invoiceDbId);
        if (updateErr) throw updateErr;
        await supabase.from('invoice_items').delete().eq('invoice_id', invoiceDbId);
      } else {
        const { data: newInv, error: createErr } = await supabase.from('invoices').insert(invoiceData).select().single();
        if (createErr) throw createErr;
        invoiceDbId = newInv.id;
        setInvoice(prev => ({ ...prev, id: newInv.id }));
      }

      const currentItems = lineItemsRef.current;
      const itemsToInsert = currentItems
        .filter(li => li.product_id)
        .map((li, idx) => ({
          invoice_id: invoiceDbId,
          company_id: companyId,
          product_id: li.product_id || null,
          variant_id: li.variant_id || null,
          variant_name: li.variant_name || '',
          product_name: li.product_name || '',
          description: li.description || li.product_name || '',
          quantity: parseFloat(li.quantity) || 0,
          unit_type: li.unit_type || 'Case',
          units_per_case: li.units_per_case || null,
          unit_price: parseFloat(li.unit_price) || 0,
          total_price: parseFloat(li.line_total || li.total_price) || 0,
          line_order: idx
        }));

      if (itemsToInsert.length > 0) {
        const { error: itemsErr } = await supabase.from('invoice_items').insert(itemsToInsert);
        if (itemsErr) throw itemsErr;
      }

      setSuccessMessage('Invoice saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      setSaving(false);
    } catch (err) {
      console.error('Error saving invoice:', err);
      setError('Failed to save invoice: ' + err.message);
      setSaving(false);
    }
  };

  // Access Denied
  if (!canView) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '70vh', textAlign: 'center' }}>
          <Card sx={{
            p: 8, borderRadius: 3, border: `1px solid ${theme.palette.grey[400]}`, background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`, maxWidth: 600, width: '100%',
            position: 'relative', overflow: 'hidden', cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': { transform: 'translateY(-8px)', boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`, border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}` },
            '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`, opacity: 0.8, transition: 'opacity 0.3s ease' },
            '&:hover::before': { opacity: 1 }
          }}>
            <Box sx={{ p: 3, borderRadius: 3, background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`, border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`, mb: 6, display: 'inline-flex', boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}` }}>
              <ReceiptIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ mb: 3, fontWeight: 500, color: 'text.primary', fontSize: '2.5rem', letterSpacing: '-0.8px' }}>Access Restricted</Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', mb: 4, fontWeight: 500, fontSize: '1.2rem' }}>Invoice Permissions Required</Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 6, fontSize: '1.05rem', maxWidth: 450, mx: 'auto' }}>
              You don't currently have the necessary permissions to access Invoice Creation. Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button variant="outlined" onClick={() => navigate(-1)} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500, px: 4, py: 1.5, fontSize: '1rem' }}>
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Card sx={{ borderRadius: 3, mt: 10, mx: 'auto', maxWidth: 600, p: 4, textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <LinearProgress sx={{ mb: 3 }} />
          <Typography variant="h6" color="primary.main">Loading Invoice Form...</Typography>
        </Card>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Fade in timeout={800}>
        <Box>
          <Container maxWidth="xl" sx={{ py: 3, pb: 8, px: { xs: 2, sm: 3 }, maxWidth: '100vw !important' }}>
            {/* Back to Invoices — above header, right-aligned */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, mb: 1 }}>
              <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/company/${companyId}/invoices`)}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
                Back to Invoices
              </Button>
            </Box>

            {/* Page Header */}
            <Card sx={{ borderRadius: 3, mb: 3, width: 'auto', border: `1px solid ${theme.palette.grey[400]}`, background: theme.palette.grey[100], boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}` }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Box sx={{ p: 2, borderRadius: 3, background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`, boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}` }}>
                      <ReceiptIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                    </Box>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 500, color: 'primary.main', fontSize: '2rem' }}>
                        {isEdit ? `Edit Invoice #${invoice.invoice_number}` : 'New Invoice'}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" fontWeight={500}>{company?.name || 'Loading...'}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    {canEdit && (
                      <Button variant="outlined" startIcon={<AutoFillIcon />} onClick={() => setAutofillOpen(true)}
                        sx={{ borderRadius: 3, px: 3, py: 1.5, fontWeight: 500, textTransform: 'none', border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, color: 'text.secondary', '&:hover': { border: `1px solid ${theme.palette.primary.main}`, color: 'primary.main', transform: 'translateY(-1px)' } }}>
                        Autofill from Order
                      </Button>
                    )}
                    <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => setPrintDialogOpen(true)}
                      sx={{ borderRadius: 3, px: 3, py: 1.5, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, '&:hover': { border: `1px solid ${theme.palette.primary.main}`, transform: 'translateY(-1px)' } }}>
                      Preview
                    </Button>
                    <Button variant="contained" startIcon={<SaveIcon />} onClick={canEdit ? handleSave : undefined} disabled={saving}
                      sx={{ borderRadius: 3, px: 4, py: 1.5, background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`, opacity: canEdit ? 1 : 0.3, pointerEvents: canEdit ? 'auto' : 'none', boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`, '&:hover': { transform: canEdit ? 'translateY(-2px)' : 'none' }, transition: 'all 0.3s ease' }}>
                      {saving ? 'Saving...' : 'Save Invoice'}
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }} onClose={() => setError(null)}>{error}</Alert>}
            {successMessage && <Alert severity="success" sx={{ mb: 3, borderRadius: 3, bgcolor: alpha(theme.palette.success.main, 0.1) }} onClose={() => setSuccessMessage('')}>{successMessage}</Alert>}

            {/* 1. Invoice Details */}
            <CollapsibleSection title="Invoice Details" expanded={!!accordion.invoiceDetails} onToggle={() => toggleSection('invoiceDetails')}>
              <DetailsSections invoice={invoice} onUpdate={handleInvoiceUpdate} canEdit={canEdit} />
            </CollapsibleSection>

            {/* 2. Customer Information */}
            <CollapsibleSection title="Customer Information" expanded={!!accordion.customerInfo} onToggle={() => toggleSection('customerInfo')}>
              <CustomerInformationSection invoice={invoice} customers={customers} onUpdate={handleInvoiceUpdate} companyId={companyId} canEdit={canEdit} />
            </CollapsibleSection>

            {/* 3. Line Items */}
            <CollapsibleSection title="Line Items" expanded={!!accordion.lineItems} onToggle={() => toggleSection('lineItems')}>
              <LineItemSection
                lineItems={lineItems} invoice={invoice} priceLists={priceLists} currentCompany={company}
                onLineItemsChange={(items) => { setLineItems(items); recalculateTotals(items, invoice); }}
                onLineItemChange={handleLineItemChange}
                onRemoveLineItem={handleRemoveLineItem}
                onUpdate={handleInvoiceUpdate}
                companyId={companyId} canEdit={canEdit} canDelete={canDelete}
              />
            </CollapsibleSection>

            {/* 4. Payment Terms */}
            <CollapsibleSection title="Payment Terms" expanded={!!accordion.paymentTerms} onToggle={() => toggleSection('paymentTerms')}>
              <PaymentTermsSection invoice={invoice} onUpdate={handleInvoiceUpdate} canEdit={canEdit} />
            </CollapsibleSection>

            {/* 5. Summary */}
            <CollapsibleSection title="Summary" expanded={!!accordion.summary} onToggle={() => toggleSection('summary')}>
              <SummarySection invoice={invoice} onUpdate={handleInvoiceUpdate} canEdit={canEdit} />
            </CollapsibleSection>

            {/* 6. Additional Notes */}
            <CollapsibleSection title="Additional Notes" expanded={!!accordion.additionalNotes} onToggle={() => toggleSection('additionalNotes')}>
              <AdditionalNotesSection invoice={invoice} onUpdate={handleInvoiceUpdate} canEdit={canEdit} />
            </CollapsibleSection>

            {/* 7. Terms & Conditions */}
            <CollapsibleSection title="Terms & Conditions" expanded={!!accordion.termsConditions} onToggle={() => toggleSection('termsConditions')}>
              <TermsAndConditionsSection invoice={invoice} onUpdate={handleInvoiceUpdate} canEdit={canEdit} canDelete={canDelete} />
            </CollapsibleSection>

            {/* 8. Prepared By */}
            <CollapsibleSection title="Prepared By" expanded={!!accordion.preparedBy} onToggle={() => toggleSection('preparedBy')}>
              <PreparedBySection invoice={invoice} onUpdate={handleInvoiceUpdate} canEdit={canEdit} />
            </CollapsibleSection>

            {/* Bottom Actions */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
              <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/company/${companyId}/invoices`)}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
                Back to Invoices
              </Button>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => setPrintDialogOpen(true)}
                  sx={{ borderRadius: 3, px: 3, py: 1.5, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, '&:hover': { border: `1px solid ${theme.palette.primary.main}`, transform: 'translateY(-1px)' } }}>
                  Preview & Print
                </Button>
                <Button variant="contained" startIcon={<SaveIcon />} onClick={canEdit ? handleSave : undefined} disabled={saving}
                  sx={{ borderRadius: 3, px: 4, py: 1.5, background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`, opacity: canEdit ? 1 : 0.3, pointerEvents: canEdit ? 'auto' : 'none' }}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </Box>
            </Box>

            {/* Autofill from Order Dialog */}
            <AutofillOrderDialogue
              open={autofillOpen}
              onClose={() => setAutofillOpen(false)}
              onAutofill={handleAutofill}
              companyId={companyId}
            />

            {/* Print Preview Dialog */}
            <PrintPreview
              open={printDialogOpen}
              onClose={() => setPrintDialogOpen(false)}
              invoice={invoice}
              lineItems={lineItems}
              company={company}
              customers={customers}
              onPrint={() => {
                printInvoice(invoice, lineItems, company, customers);
                setPrintDialogOpen(false);
              }}
            />
          </Container>
        </Box>
      </Fade>
    </LocalizationProvider>
  );
};

export default InvoiceCreation;
